import moment from 'moment'
import { RootState } from '.'
import type { AnyPromosAction } from '../actions/promos'
import type { Promo, PromoBrand } from '../types'
import { ArrayUtils } from '../utils'

const initialState = {
    brands: [] as PromoBrand[],
    promos: [] as Promo[],
    types: [] as { type: string }[],
    error: undefined as string | undefined,
    isWorking: false,
    selectedPromo: {
        data: undefined as Promo | undefined,
        error: undefined as string | undefined,
        isWorking: false,
    },
    assetUpload: {
        isUploading: false,
        progress: undefined as number | undefined,
    }
}

export type PromosState = typeof initialState

type RawPromo = Promo

function formatPromoData(rawPromo: RawPromo): Promo {
    const parsedExceptionSkus = Array.isArray(rawPromo.exception_skus)
        ? rawPromo.exception_skus
        : (rawPromo.exception_skus ?? '')
            .toString()
            .split(',')
            .map(sku => sku.trim())
            .filter(sku => !!sku)

    return {
        ...rawPromo,
        date_start: moment(rawPromo.date_start, 'YYYY-MM-DD').toDate(),
        date_end: moment(rawPromo.date_end, 'YYYY-MM-DD').toDate(),
        exception_skus: parsedExceptionSkus,
        exception_dates: rawPromo.exception_dates.map(
            date => moment(date, 'YYYY-MM-DD').toDate(),
        ),
    }
}

function promosReducer(state: PromosState = initialState, { type, payload }: AnyPromosAction) {
    switch (type) {
        case 'PROMOS_FETCH##REQUEST':
            return {
                ...state,
                isWorking: true,
                error: undefined,
                promos: initialState.promos,
            }
        case 'PROMOS_FETCH##SUCCESS':
            return {
                ...state,
                promos: payload.map(formatPromoData),
                isWorking: false,
                error: undefined,
            }
        case 'PROMOS_FETCH##FAILURE':
            return {
                ...state,
                isWorking: false,
                error: payload.message,
            }
        case 'PROMO_ID_FETCH##REQUEST':
            return {
                ...state,
                selectedPromo: {
                    data: undefined,
                    isWorking: true,
                    error: undefined,
                },
            }
        case 'PROMO_ID_FETCH##SUCCESS':
            return {
                ...state,
                selectedPromo: {
                    data: formatPromoData(payload),
                    isWorking: false,
                    error: undefined,
                },
            }
        case 'PROMO_ID_FETCH##FAILURE':
            return {
                ...state,
                selectedPromo: {
                    data: undefined,
                    isWorking: false,
                    error: payload.message,
                },
            }
        case 'PROMO_BRAND_FETCH##REQUEST':
            return {
                ...state,
                brands: initialState.brands,
            }
        case 'PROMO_BRAND_FETCH##SUCCESS':
            return {
                ...state,
                brands: payload,
            }
        case 'PROMO_TYPES_FETCH##REQUEST':
            return {
                ...state,
                types: initialState.types,
            }
        case 'PROMO_TYPES_FETCH##SUCCESS':
            return {
                ...state,
                types: payload,
            }
        case 'PROMO_DELETE##SUCCESS': {
            const index = state.promos.findIndex(promo => promo.id === payload)
            const updatedPromos = ArrayUtils.removeItem(state.promos, index)
            let { selectedPromo } = state

            if (selectedPromo.data?.id === payload) {
                selectedPromo = initialState.selectedPromo
            }

            return {
                ...state,
                promos: updatedPromos,
                selectedPromo,
            }
        }
        case 'PROMO_UPDATE##SUCCESS': {
            const index = state.promos.findIndex(promo => promo.id === payload.id)
            const updatedPromos = ArrayUtils.insertItem(
                state.promos,
                index,
                formatPromoData(payload),
            )

            return {
                ...state,
                promos: updatedPromos,
            }
        }
        case 'COMPLETE_PROMO_UPLOAD##SUCCESS': {
            return {
                ...state,
                promos: [
                    ...state.promos,
                    formatPromoData(payload),
                ],
            }
        }
        default:
        return state
    }
}

function isWorking(state: RootState): boolean {
    return state.promos.isWorking
}

function selectPromos(state: RootState): Promo[] {
    return state.promos.promos
}

function isLoadingSelectedPromo(state: RootState): boolean {
    return state.promos.selectedPromo.isWorking
}

function selectedPromo(state: RootState): Promo | undefined {
    return state.promos.selectedPromo.data
}

function selectBrands(state: RootState): PromoBrand[] {
    return state.promos.brands
}

function selectPromoTypes(state: RootState): { type: string }[] {
    return state.promos.types
}

export default promosReducer

export {
    isLoadingSelectedPromo,
    isWorking,
    selectBrands,
    selectPromos,
    selectedPromo,
    selectPromoTypes,
}
